/**
 * ContentSelector
 *
 * Checkbox list of playbook sections for selecting which plays to export.
 * Each section shows its name and play count, with a checkbox for selection.
 */

import { Checkbox } from '../ui/Checkbox'
import type { ExportSection as Section } from '@/components/playbook-editor/Types'

interface ContentSelectorProps {
	sections: Section[]
	selectedSectionIds: Set<string>
	onToggleSection: (sectionId: string) => void
	onSelectAll: () => void
	onDeselectAll: () => void
}

export function ContentSelector({
	sections,
	selectedSectionIds,
	onToggleSection,
	onSelectAll,
	onDeselectAll,
}: ContentSelectorProps) {
	const allSelected = sections.length > 0 && sections.every((s) => selectedSectionIds.has(s.id))

	return (
		<div className="content-selector">
			<div className="content-selector__header">
				<span className="content-selector__label">Content</span>
				<button
					className="content-selector__toggle-btn"
					onClick={allSelected ? onDeselectAll : onSelectAll}
				>
					{allSelected ? 'Deselect all' : 'Select all'}
				</button>
			</div>

			<div className="content-selector__list">
				{sections.map((section) => {
					const isSelected = selectedSectionIds.has(section.id)
					const playCount = section.plays.length

					return (
						<label
							key={section.id}
							className="content-selector__item"
							data-selected={isSelected}
						>
							<Checkbox
								checked={isSelected}
								onCheckedChange={() => onToggleSection(section.id)}
							/>
							<span className="content-selector__name">{section.name}</span>
							<span className="content-selector__count">
								{playCount} {playCount === 1 ? 'play' : 'plays'}
							</span>
						</label>
					)
				})}

				{sections.length === 0 && (
					<p className="content-selector__empty">No sections in this playbook</p>
				)}
			</div>
		</div>
	)
}
